import React__default, { memo, useState, useMemo } from 'react';
import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Handle } from './handle.js';
import { Highlight } from './highlight.js';
import { Track } from './track.js';

var _excluded = ["name", "onChange", "value", "minValue", "maxValue", "children", "stepSize", "orientation", "exponentiation"];
var closest = function closest(value, arr) {
  return arr.reduce(function (prev, curr) {
    return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
  });
};
var RangeSliderNonLinear = /*#__PURE__*/memo(function (rawProps) {
  var name = rawProps.name,
    onChange = rawProps.onChange,
    value = rawProps.value,
    _rawProps$minValue = rawProps.minValue,
    minValue = _rawProps$minValue === void 0 ? 0 : _rawProps$minValue,
    maxValue = rawProps.maxValue,
    children = rawProps.children,
    _rawProps$stepSize = rawProps.stepSize,
    stepSize = _rawProps$stepSize === void 0 ? 1 : _rawProps$stepSize,
    _rawProps$orientation = rawProps.orientation,
    orientation = _rawProps$orientation === void 0 ? "horizontal" : _rawProps$orientation,
    _rawProps$exponentiat = rawProps.exponentiation,
    exponentiation = _rawProps$exponentiat === void 0 ? 2 : _rawProps$exponentiat,
    restProps = _objectWithoutPropertiesLoose(rawProps, _excluded);

  /**
   * The non linear slider applies exponential increase spanning
   * between the min and max value. This makes the lower numbers
   * take up a larger protion of the slider span. The severety of
   * the increase is calculated based on the exponentiation value.
   * More info https://easings.net/
   */

  var percentToValue = function percentToValue(percentage, nthPow) {
    percentage = Math.min(100, Math.max(0, percentage));
    var exponent = percentage / 100;
    var calculation = Math.pow(exponent, nthPow);
    var value = Math.round(minValue + (maxValue - minValue) * calculation);
    return value;
  };
  var valueToPercent = function valueToPercent(value, nthRoot) {
    value = Math.min(maxValue, Math.max(minValue, value));
    var fraction = (value - minValue) / (maxValue - minValue);
    var exponent = Math.pow(fraction, 1 / nthRoot);
    var percent = exponent * 100;
    return percent;
  };
  var _useState = useState(valueToPercent(value.min, exponentiation)),
    minValuePercent = _useState[0],
    setMinValuePercent = _useState[1];
  var _useState2 = useState(valueToPercent(value.max, exponentiation)),
    maxValuePercent = _useState2[0],
    setMaxValuePercent = _useState2[1];
  var onChangeMin = function onChangeMin(v) {
    if (v < maxValuePercent) {
      setMinValuePercent(v);
      var val = percentToValue(v, exponentiation);
      onChange({
        min: val < value.max ? val : value.max - stepSize,
        max: value.max
      });
    }
  };
  var onChangeMax = function onChangeMax(v) {
    if (v > minValuePercent) {
      setMaxValuePercent(v);
      var val = percentToValue(v, exponentiation);
      onChange({
        min: value.min,
        max: val > value.min ? val : value.min + stepSize
      });
    }
  };
  useMemo(function () {
    if (maxValuePercent === 100 && value.max !== maxValue) {
      setMaxValuePercent(valueToPercent(value.max, exponentiation));
    }
    if (valueToPercent(value.min, exponentiation) !== minValuePercent) {
      setMinValuePercent(valueToPercent(value.min, exponentiation));
    }
    if (valueToPercent(value.max, exponentiation) !== maxValuePercent) {
      setMaxValuePercent(valueToPercent(value.max, exponentiation));
    }
  }, [value, minValue, maxValue]);
  return /*#__PURE__*/React__default.createElement(Track, _extends({}, restProps, {
    minValue: 0,
    maxValue: 100,
    orientation: orientation,
    stepSize: stepSize,
    variant: rawProps.variant,
    onClick: function onClick(v) {
      var val = percentToValue(v, exponentiation);
      var c = closest(val, [value.min, value.max]);
      if (c === value.min) {
        setMinValuePercent(v);
      }
      if (c === value.max) {
        setMaxValuePercent(v);
      }
      onChange({
        min: c === value.min ? val : value.min,
        max: c === value.max ? val : value.max
      });
    }
  }), /*#__PURE__*/React__default.createElement("input", {
    name: (typeof name === "undefined" ? "" : name) + "_start",
    value: minValuePercent,
    type: "hidden"
  }), /*#__PURE__*/React__default.createElement("input", {
    name: (typeof name === "undefined" ? "" : name) + "_end",
    value: maxValuePercent,
    type: "hidden"
  }), /*#__PURE__*/React__default.createElement(Highlight, {
    minValue: 0,
    start: minValuePercent,
    end: maxValuePercent
  }), /*#__PURE__*/React__default.createElement(Handle, {
    "aria-valuemin": 0,
    "aria-valuemax": 100,
    value: minValuePercent,
    onChange: minValuePercent < maxValuePercent ? onChangeMin : function () {}
  }), /*#__PURE__*/React__default.createElement(Handle, {
    "aria-valuemin": 0,
    "aria-valuemax": 100,
    value: maxValuePercent,
    onChange: maxValuePercent > minValuePercent ? onChangeMax : function () {}
  }), children);
});

export { RangeSliderNonLinear };
//# sourceMappingURL=range-slider-non-linear.js.map
